import { clamp } from "@/utility/clamp";
import { isValidColorValue } from "@/utility/color";
import { parseHexString } from "@mm/color-lib";
import type { Expression } from "mapbox-gl";

export type IsoBandLegendItem = {
  color: string;
  label: string;
};

export type IsoBandsFillMode = "random" | "monochromatic";

export type IsoBandValueEntry = {
  kind: "value";
  value: number;
  label: string;
  key: string;
};

export type IsoBandIntervalEntry = {
  kind: "interval";
  lower: number;
  upper: number;
  label: string;
  key: string;
};

export type IsoBandEntry = IsoBandValueEntry | IsoBandIntervalEntry;

type ResolveIsoBandEntriesArgs = {
  values?: string | null;
  valueRange?: string | null;
  isobandIntervals?: string | null;
};

type ReconcileIsoBandFillColorsArgs = {
  previous: ResolveIsoBandEntriesArgs;
  next: ResolveIsoBandEntriesArgs;
  baseColor: string;
  fillColors?: string | null;
  fillColorMode?: string | null;
};

type ResolveIsoBandColorsArgs = ResolveIsoBandEntriesArgs & {
  baseColor: string;
  fillColors?: string | null;
  fillColorMode?: string | null;
  fillReverse?: string | null;
};

type BuildIsoBandLegendItemsArgs = ResolveIsoBandColorsArgs & {
  unit?: string;
};

const DEFAULT_MODE: IsoBandsFillMode = "random";

const parseNumber = (value: string): number | null => {
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumberLabel = (value: number): string => `${value}`;

const parseValuesEntries = (rawValue: string): IsoBandEntry[] =>
  rawValue
    .split(",")
    .map((part) => parseNumber(part))
    .filter((value): value is number => value !== null)
    .map((value) => ({
      kind: "value" as const,
      value,
      label: formatNumberLabel(value),
      key: `value:${value}`,
    }));

const parseRangeEntries = (rawValue: string): IsoBandEntry[] => {
  const parts = rawValue.split(",").map((part) => parseNumber(part));
  if (parts.length !== 3 || parts.some((part) => part === null)) {
    return [];
  }

  const [start, end, step] = parts as [number, number, number];
  if (step === 0) {
    return [];
  }

  const direction = start <= end ? 1 : -1;
  const normalizedStep = Math.abs(step) * direction;
  const values: number[] = [];
  const epsilon = Math.abs(normalizedStep) / 1000;

  for (
    let current = start;
    direction > 0 ? current <= end + epsilon : current >= end - epsilon;
    current += normalizedStep
  ) {
    const rounded = Number(current.toFixed(12));
    values.push(rounded);
  }

  return values.map((value) => ({
    kind: "value" as const,
    value,
    label: formatNumberLabel(value),
    key: `value:${value}`,
  }));
};

const parseIntervalEntries = (rawValue: string): IsoBandEntry[] =>
  rawValue
    .split("_")
    .map((part) => {
      const [lowerRaw, upperRaw] = part.split(",");
      if (lowerRaw == null || upperRaw == null) {
        return null;
      }

      const lower = parseNumber(lowerRaw);
      const upper = parseNumber(upperRaw);
      if (lower === null || upper === null) {
        return null;
      }

      return {
        kind: "interval" as const,
        lower,
        upper,
        label: `${formatNumberLabel(lower)} - ${formatNumberLabel(upper)}`,
        key: `interval:${lower}:${upper}`,
      };
    })
    .filter((entry): entry is IsoBandIntervalEntry => entry !== null);

type ParsedColor = {
  r: number;
  g: number;
  b: number;
  a: number;
};

const parseRgbChannel = (rawValue: string): number | null => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) {
    return null;
  }

  return value <= 1 ? clamp(value, 0, 1) * 255 : clamp(value, 0, 255);
};

const parseAlphaChannel = (rawValue: string): number | null => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) {
    return null;
  }

  return clamp(value, 0, 1);
};

const parseColor = (color: string): ParsedColor | null => {
  const trimmed = color.trim();
  const parsedHex = parseHexString(trimmed);
  if (parsedHex) {
    return {
      r: parsedHex[0] * 255,
      g: parsedHex[1] * 255,
      b: parsedHex[2] * 255,
      a: parsedHex[3],
    };
  }

  const rgbaMatch = trimmed.match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbaMatch) {
    return null;
  }

  const parts = rgbaMatch[1].split(",").map((part) => part.trim());
  if (parts.length !== 3 && parts.length !== 4) {
    return null;
  }

  const r = parseRgbChannel(parts[0]);
  const g = parseRgbChannel(parts[1]);
  const b = parseRgbChannel(parts[2]);
  const a = parts[3] == null ? 1 : parseAlphaChannel(parts[3]);

  if (r === null || g === null || b === null || a === null) {
    return null;
  }

  return { r, g, b, a };
};

const rgbToHex = (r: number, g: number, b: number) =>
  `#${[r, g, b].map((value) => clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0")).join("")}`;

const applyAlphaToColor = (r: number, g: number, b: number, alpha: number): string => {
  if (alpha >= 0.999) {
    return rgbToHex(r, g, b);
  }

  return `rgba(${Math.round(clamp(r, 0, 255))}, ${Math.round(clamp(g, 0, 255))}, ${Math.round(
    clamp(b, 0, 255),
  )}, ${alpha.toFixed(2)})`;
};

const rgbToHsl = (r: number, g: number, b: number) => {
  const normalizedR = r / 255;
  const normalizedG = g / 255;
  const normalizedB = b / 255;
  const max = Math.max(normalizedR, normalizedG, normalizedB);
  const min = Math.min(normalizedR, normalizedG, normalizedB);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  let hue = 0;
  if (delta !== 0) {
    if (max === normalizedR) {
      hue = 60 * (((normalizedG - normalizedB) / delta) % 6);
    } else if (max === normalizedG) {
      hue = 60 * ((normalizedB - normalizedR) / delta + 2);
    } else {
      hue = 60 * ((normalizedR - normalizedG) / delta + 4);
    }
  }

  return {
    h: (hue + 360) % 360,
    s: saturation * 100,
    l: lightness * 100,
  };
};

const hslToRgb = (h: number, s: number, l: number) => {
  const normalizedS = clamp(s, 0, 100) / 100;
  const normalizedL = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * normalizedL - 1)) * normalizedS;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = normalizedL - c / 2;

  let rPrime = 0;
  let gPrime = 0;
  let bPrime = 0;

  if (h >= 0 && h < 60) {
    rPrime = c;
    gPrime = x;
  } else if (h < 120) {
    rPrime = x;
    gPrime = c;
  } else if (h < 180) {
    gPrime = c;
    bPrime = x;
  } else if (h < 240) {
    gPrime = x;
    bPrime = c;
  } else if (h < 300) {
    rPrime = x;
    bPrime = c;
  } else {
    rPrime = c;
    bPrime = x;
  }

  return {
    r: (rPrime + m) * 255,
    g: (gPrime + m) * 255,
    b: (bPrime + m) * 255,
  };
};

const mixRgb = (
  source: { r: number; g: number; b: number },
  target: { r: number; g: number; b: number },
  amount: number,
) => ({
  r: source.r + (target.r - source.r) * amount,
  g: source.g + (target.g - source.g) * amount,
  b: source.b + (target.b - source.b) * amount,
});

export const parseIsoBandsFillColors = (fillColors?: string | null): string[] => {
  if (!fillColors) {
    return [];
  }

  try {
    const parsed = JSON.parse(fillColors);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter((value) => isValidColorValue(value));
  } catch {
    return [];
  }
};

export const serializeIsoBandsFillColors = (colors: string[]): string | null => {
  const normalized = colors.map((color) => color.trim()).filter((color) => isValidColorValue(color));
  return normalized.length > 0 ? JSON.stringify(normalized) : null;
};

export const parseIsoBandsFillMode = (fillColorMode?: string | null): IsoBandsFillMode =>
  fillColorMode === "monochromatic" ? "monochromatic" : DEFAULT_MODE;

export const parseIsoBandsFillReverse = (fillReverse?: string | null): boolean => fillReverse === "true";

export const resolveIsoBandEntries = (args: ResolveIsoBandEntriesArgs): IsoBandEntry[] => {
  if (args.isobandIntervals?.trim()) {
    return parseIntervalEntries(args.isobandIntervals);
  }
  if (args.values?.trim()) {
    return parseValuesEntries(args.values);
  }
  if (args.valueRange?.trim()) {
    return parseRangeEntries(args.valueRange);
  }
  return [];
};

export const createRandomIsoBandColors = (count: number, baseColor: string): string[] => {
  if (count <= 0) {
    return [];
  }

  const parsedBaseColor = parseColor(baseColor);
  if (!parsedBaseColor) {
    return Array.from({ length: count }, () => baseColor);
  }

  const { h, s, l } = rgbToHsl(parsedBaseColor.r, parsedBaseColor.g, parsedBaseColor.b);
  return Array.from({ length: count }, (_, index) => {
    if (index === 0) {
      return baseColor;
    }

    const hue = (h + index * 47) % 360;
    const saturation = clamp(s + (index % 2 === 0 ? 6 : -4), 35, 90);
    const lightness = clamp(l + ((index % 3) - 1) * 10, 28, 78);
    const rgb = hslToRgb(hue, saturation, lightness);
    return applyAlphaToColor(rgb.r, rgb.g, rgb.b, parsedBaseColor.a);
  });
};

export const createMonochromaticIsoBandColors = (count: number, baseColor: string): string[] => {
  if (count <= 0) {
    return [];
  }

  const parsedBaseColor = parseColor(baseColor);
  if (!parsedBaseColor) {
    return Array.from({ length: count }, () => baseColor);
  }

  if (count === 1) {
    return [baseColor];
  }

  const midpoint = (count - 1) / 2;
  return Array.from({ length: count }, (_, index) => {
    const distance = midpoint === 0 ? 0 : (index - midpoint) / midpoint;
    if (Math.abs(distance) < 0.001) {
      return baseColor;
    }

    const mixed =
      distance < 0
        ? mixRgb(parsedBaseColor, { r: 255, g: 255, b: 255 }, Math.abs(distance) * 0.55)
        : mixRgb(parsedBaseColor, { r: 0, g: 0, b: 0 }, Math.abs(distance) * 0.55);

    return applyAlphaToColor(mixed.r, mixed.g, mixed.b, parsedBaseColor.a);
  });
};

export const resolveIsoBandColors = (args: ResolveIsoBandColorsArgs): string[] => {
  const entries = resolveIsoBandEntries(args);
  const count = entries.length;
  const mode = parseIsoBandsFillMode(args.fillColorMode);
  const reverse = parseIsoBandsFillReverse(args.fillReverse);
  const storedColors = parseIsoBandsFillColors(args.fillColors);

  let colors =
    mode === "monochromatic"
      ? createMonochromaticIsoBandColors(count, args.baseColor)
      : storedColors.length === count
        ? storedColors
        : createRandomIsoBandColors(count, args.baseColor);

  if (reverse) {
    colors = colors.slice().reverse();
  }
  return colors;
};

export const reconcileIsoBandFillColors = (args: ReconcileIsoBandFillColorsArgs): string | null => {
  if (parseIsoBandsFillMode(args.fillColorMode) !== "random") {
    return null;
  }

  const previousEntries = resolveIsoBandEntries(args.previous);
  const nextEntries = resolveIsoBandEntries(args.next);

  if (nextEntries.length === 0) {
    return null;
  }

  const storedColors = parseIsoBandsFillColors(args.fillColors);
  const previousColors =
    storedColors.length === previousEntries.length
      ? storedColors
      : createRandomIsoBandColors(previousEntries.length, args.baseColor);
  const previousColorsByKey = new Map(
    previousEntries.map((entry, index) => [entry.key, previousColors[index] ?? args.baseColor]),
  );
  const defaultNextColors = createRandomIsoBandColors(nextEntries.length, args.baseColor);
  const nextColors = nextEntries.map((entry, index) => previousColorsByKey.get(entry.key) ?? defaultNextColors[index]);

  return serializeIsoBandsFillColors(nextColors);
};

export const buildIsoBandLegendItems = (args: BuildIsoBandLegendItemsArgs): IsoBandLegendItem[] => {
  const entries = resolveIsoBandEntries(args);
  const colors = resolveIsoBandColors(args);
  const suffix = args.unit?.trim() ? ` ${args.unit.trim()}` : "";

  return entries.map((entry, index) => ({
    color: colors[index] ?? args.baseColor,
    label: `${entry.label}${suffix}`,
  }));
};

export const buildIsoBandsFillColorExpression = (args: ResolveIsoBandColorsArgs): string | Expression => {
  const entries = resolveIsoBandEntries(args);
  const colors = resolveIsoBandColors(args);

  if (entries.length === 0 || colors.length === 0) {
    return args.baseColor;
  }

  const valueEntries = entries.filter((entry): entry is IsoBandValueEntry => entry.kind === "value");
  if (valueEntries.length === entries.length) {
    const expression: Expression = ["match", ["to-string", ["get", "isovalue"]]];
    valueEntries.forEach((entry, index) => {
      expression.push(String(entry.value), colors[index] ?? args.baseColor);
    });
    expression.push(args.baseColor);
    return expression;
  }

  const intervalEntries = entries.filter((entry): entry is IsoBandIntervalEntry => entry.kind === "interval");
  if (intervalEntries.length === entries.length) {
    const expression: Expression = [
      "match",
      ["concat", ["to-string", ["get", "lower_isovalue"]], ":", ["to-string", ["get", "upper_isovalue"]]],
    ];
    intervalEntries.forEach((entry, index) => {
      expression.push(`${entry.lower}:${entry.upper}`, colors[index] ?? args.baseColor);
    });
    expression.push(args.baseColor);
    return expression;
  }

  return args.baseColor;
};
